import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withHandlers, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { ReportType, reportGraphSizes, Size } from 'types';
import { GraphDefaultOption } from './GraphDefaultOption';
import { GraphTypeOption } from './GraphTypeOption';
import { GraphSizeOption } from './GraphSizeOption';
import { getNumberColumns } from 'logic/reports/reportType';

export type OwnProps = {
    reportType: ReportType;
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const styles = {
    indented: {
        marginLeft: '2em',
    },
};

type Props = OwnProps & RecomposeProps & TranslateProps;

const RawGraphSection = ({
    reportType,
    numberColumnNames,
    graphDefault,
    graphType,
    graphSize,
    handleChangeDefault,
    handleChangeType,
    handleChangeSize,
    t,
}: Props) =>
    <Fragment>
        <div>
            <h3>
                { t('6.1241.0.61') }
            </h3>
            <div style={ styles.indented }>
                { numberColumnNames.map((name, i) =>
                    <GraphDefaultOption
                        key={ name }
                        reportType={ reportType }
                        name={ name }
                        checked={ graphDefault === name }
                        onChange={ handleChangeDefault }
                    />
                ) }
            </div>
        </div>
        <div>
            <h3>
                { t('6.1241.0.62') }
            </h3>
            <div style={ styles.indented }>
                <GraphTypeOption
                    type="Line"
                    checked={ graphType === 'Line' }
                    onChange={ handleChangeType }
                />
                <GraphTypeOption
                    type="Bar"
                    checked={ graphType === 'Bar' }
                    onChange={ handleChangeType }
                />
            </div>
        </div>
        <div>
            <h3>
                { t('6.1241.0.63') }
            </h3>
            <div style={ styles.indented }>
                { reportGraphSizes.map((s, i) =>
                    <GraphSizeOption
                        key={ s.id }
                        size={ s }
                        checked={ graphSize === i }
                        onChange={ handleChangeSize }
                    />
                ) }
            </div>
        </div>
    </Fragment>
;

type RecomposeProps = {
    numberColumnNames: string[];
    graphDefault: string;
    graphType: 'Bar' | 'Line';
    graphSize: number;
    handleChangeDefault: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeType: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeSize: React.ChangeEventHandler<HTMLInputElement>;
};

const chooseClosestGraphSize = ({ width, height }: Size) =>
    reportGraphSizes.map((s, index) => ({
        error: Math.pow((s.width - width) * (s.height - height), 2),
        index,
    }))
    .sort((a, b) => a.error - b.error)
    .shift()!
    .index
;

export const GraphSection = compose<Props, OwnProps>(
    setDisplayName('GraphSection'),
    translate(),
    withProps<Partial<Props>, OwnProps>(({ reportType, reportSettings }) => ({
        graphDefault: reportSettings.graphDefaultData,
        graphType: reportSettings.graphType,
        graphSize: chooseClosestGraphSize(reportSettings.graphSize),
        numberColumnNames: getNumberColumns(reportType),
    })),
    withHandlers<OwnProps, Partial<RecomposeProps>>({
        handleChangeDefault: ({ reportSettings, onChange }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const defaultName = e.currentTarget.value;
            onChange(reportSettings => reportSettings.setGraphDefaultData(defaultName));
        },
        handleChangeType: ({ reportSettings, onChange }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const type = e.currentTarget.value as 'Bar' | 'Line';
            onChange(reportSettings => reportSettings.setGraphType(type));
        },
        handleChangeSize: ({ reportSettings, onChange }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const sizeId = e.currentTarget.value;
            const size = reportGraphSizes.find(s => s.id === sizeId);
            if (size) {
                onChange(reportSettings => reportSettings.setGraphSize(size.width, size.height));
            }
        },
    }),
)(RawGraphSection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/graph/GraphSection.tsx